import { useState } from 'react';

import { Col, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  doCancelOrder,
  doClearCancelOrder,
  doClearGetQuote,
  doClearGetQuoteChart,
  doClearUpdateOrder,
  doGetOrder,
} from 'src/actions';
import { DateConstant, FilterBarConstant } from 'src/constants';
import { OrderTypeDto } from 'src/dtos';
import { useOrders, useSubmittedAccountSelector } from 'src/hooks';
import {
  CancelOrderButton,
  ChangeOrderButton,
  ChangeOrderModal,
  ConfirmActionModal,
  CurrencyField,
  FilterBar,
  MCard,
  MTable,
  MTypography,
  OrderStatusTag,
} from 'src/lib';
import { Order, OrderDetails, OrderEvent, OrderStatusLabel } from 'src/models';
import { ColorConstant, FlexDirection, Spacing } from 'src/styles';
import { getOrderStatusTagColor, mapFilterToGetOrderListQueryParamsDto, Maybe } from 'src/utils';

interface OrderPriceFieldProps {
  orderType: OrderTypeDto;
  limit?: number;
  stop?: number;
  includeStopPrice?: boolean;
}

export const OrderPriceField = ({ orderType, limit, stop, includeStopPrice }: OrderPriceFieldProps) => {
  if (orderType === OrderTypeDto.Market || orderType === OrderTypeDto.MarketOnClose) {
    return <MTypography variant='subtitle1'>Market</MTypography>;
  }

  if (orderType === OrderTypeDto.StopMarket) {
    return (
      <>
        <MTypography variant='subtitle1' inline>
          Market{' '}
        </MTypography>
        {includeStopPrice && (
          <MTypography variant='subtitle2' inline>
            (Stop Price <CurrencyField value={stop} />)
          </MTypography>
        )}
      </>
    );
  }

  if (orderType === OrderTypeDto.StopLimit) {
    return (
      <>
        <MTypography variant='subtitle1' inline>
          <CurrencyField value={limit} />
        </MTypography>
        {includeStopPrice && (
          <MTypography variant='subtitle2' inline>
            (Stop Price <CurrencyField value={stop} />)
          </MTypography>
        )}
      </>
    );
  }

  return (
    <MTypography variant='subtitle1'>
      <CurrencyField value={limit} />
    </MTypography>
  );
};

export const OrderEventsTable = ({ order }: { order: Order }) => {
  const orderDetails: Maybe<OrderDetails> = useSelector((state: any) => state.orders.details?.[order.id]?.data);
  const isOrderDetailsLoading = useSelector((state: any) => state.orders.details?.[order.id]?.isLoading);

  const expandableCols: ColumnsType<OrderEvent> = [
    {
      key: 'expandPlaceholder',
      width: '50px',
    },
    {
      title: 'Details',
      key: 'details',
      render: (value: OrderEvent) => {
        return value.status.label;
      },
    },
    {
      title: 'Order Price',
      render: (value: OrderEvent) => (
        <OrderPriceField orderType={order.type.value} limit={value.limit} stop={value.stop} includeStopPrice />
      ),
    },
    {
      title: 'Fill Price',
      key: 'fillPrice',
      render: (value: OrderEvent) => {
        return <CurrencyField value={value.fillPrice} />;
      },
    },
    {
      title: 'Fill Quantity',
      dataIndex: 'fillQuantity',
    },
    {
      title: 'Updated On',
      key: 'updatedAt',
      render: (value: OrderEvent) => {
        return moment(value.createdAt).format(DateConstant.DEFAULT_DATETIME_WITH_MS_FORMAT);
      },
    },
  ];

  return (
    <MTable<OrderEvent>
      loading={isOrderDetailsLoading}
      columns={expandableCols}
      data={orderDetails?.events ?? []}
      rowKey='id'
      pagination={false}
    />
  );
};

// Change/Cancel render nothing on rows that are not actionable, so their columns need a floor to
// stop the grid reflowing between pages (same approach as the History table). Sized to clear the
// widest button ('Change' at Font.lg semibold).
const ACTION_COLUMN_MIN_WIDTH = 112;

const defaultValues: FilterBarConstant.Sample = {
  sortBy: FilterBarConstant.SortBy.Newest,
  timeline: FilterBarConstant.FixedTimeline.ThisYear,
  customTimeline: undefined,
  filterBy: [],
  show: [],
  page: 1,
};

export const OrdersTable = () => {
  const orderDetails: Maybe<Record<string, { data?: OrderDetails }>> = useSelector(
    (state: any) => state.orders.details,
  );
  const isCancelOrderLoading = useSelector((state: any) => state.orders.cancel.isLoading);
  const cancelOrderError = useSelector((state: any) => state.orders.cancel.error);
  const cancelOrderFailed = useSelector((state: any) => Boolean(state.orders.cancel.__failed));
  const cancelOrderSuccess = useSelector((state: any) => Boolean(state.orders.cancel.__succeeded));

  const [selectedCancelOrder, setSelectedCancelOrder] = useState<Order | null>(null);
  const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState(false);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState<Order | null>(null);

  const [filters, setFilters] = useState<FilterBarConstant.Sample>(defaultValues);

  const { account } = useSubmittedAccountSelector();
  const { orderList, isOrderListLoading, orderListTotal } = useOrders({
    queryParamsDto: mapFilterToGetOrderListQueryParamsDto(filters, {
      pageIndex: (filters.page ?? 1) - 1,
      pageSize: 10,
    }),
    dependency: [filters.timeline, filters.customTimeline, filters.filterBy, filters.page, account.accountId],
  });
  const dispatch = useDispatch();

  const onCancel = (order: Order) => {
    setSelectedCancelOrder(order);
    setIsCancelOrderModalOpen(true);
  };

  const onConfirmCancelOrder = (selectedItem: Order) => {
    dispatch(doCancelOrder({ params: { id: selectedItem.id, accountId: account.accountId } }));
  };

  const onChangeOrder = (order: Order) => {
    dispatch(doClearUpdateOrder());
    setSelectedChangeOrder(order);
  };

  const onCloseChangeOrder = () => {
    setSelectedChangeOrder(null);
    dispatch(doClearUpdateOrder());
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  const mapOrderStatusToStatusFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options: FilterBarConstant.GroupOptionItem[] = Object.values(OrderStatusLabel).map(value => ({
      label: <OrderStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.Order}`,
      tag: {
        text: value,
        ...getOrderStatusTagColor(value),
      },
    }));

    return [{ options }];
  };

  const onApproveFilters = (newFilters: FilterBarConstant.Sample) => {
    setFilters({ ...newFilters, page: filters.page !== 1 ? 1 : newFilters.page });
  };

  const cols: ColumnsType<Order> = [
    {
      title: 'Id',
      dataIndex: 'id',
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
    },
    {
      title: 'Order',
      render: (value: Order) => value.action.label,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
    },
    {
      title: 'Type',
      render: (value: Order) => value.type.label,
    },
    {
      title: 'Order Price',
      render: (value: Order) => <OrderPriceField orderType={value.type.value} limit={value.limit} stop={value.stop} />,
    },
    {
      title: 'Status',
      render: (value: Order) => (
        <Row className={FlexDirection.column}>
          <Col span={24}>
            <OrderStatusTag value={value.status.label} />
          </Col>
          {value.isPartial && (
            <Col span={24}>
              <MTypography variant='subtitle2'>
                Partially Filled {value.filledQuantity}/{value.quantity}
              </MTypography>
            </Col>
          )}
        </Row>
      ),
    },
    {
      title: 'Updated On',
      render: (value: Order) => moment(value.updatedAt).format(DateConstant.DEFAULT_DATETIME_FORMAT),
    },
    {
      title: 'Action',
      key: 'change',
      width: 1,
      onCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      onHeaderCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      render: (value: Order) => <ChangeOrderButton value={value} onClick={onChangeOrder} />,
    },
    {
      key: 'cancel',
      width: 1,
      onCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      onHeaderCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      render: (value: Order) => <CancelOrderButton value={value} onClick={onCancel} />,
    },
  ];

  return (
    <Row>
      <Col span={24} id='orders-section'>
        <Row>
          <Col span={24} className={Spacing.my16}>
            <MTypography variant='h4' weight='semibold' color={ColorConstant.BRAND6}>
              Securities Orders
            </MTypography>
          </Col>
          <Col span={24}>
            <FilterBar
              sortByOptions={[{ label: FilterBarConstant.SortByLabel.Newest, value: FilterBarConstant.SortBy.Newest }]}
              byFilter={mapOrderStatusToStatusFilterGroup()}
              onApprove={onApproveFilters}
              defaultValue={defaultValues}
              initValue={filters}
            />
          </Col>
          <Col span={24} className={Spacing.my16}>
            <MCard>
              <MTable<Order>
                columns={cols}
                data={orderList}
                pagination={{
                  position: ['bottomRight'],
                  showSizeChanger: false,
                  current: filters.page,
                  pageSize: 10,
                  total: orderListTotal,
                }}
                loading={isOrderListLoading}
                expandable={{
                  expandedRowRender: record => <OrderEventsTable order={record} />,
                  childrenColumnName: 'none',
                  columnWidth: '50px',
                  onExpand: (expanded, record) => {
                    if (expanded && isEmpty(orderDetails?.[record.id]?.data)) {
                      dispatch(doGetOrder({ params: { id: record.id, accountId: account.accountId } }));
                    }
                  },
                }}
                rowKey={record => record.id}
                onChange={pagination => {
                  setFilters({ ...filters, page: pagination.current ?? 1 });
                }}
                testId={'orders-table'}
              />
            </MCard>
          </Col>
        </Row>
      </Col>
      {selectedCancelOrder && (
        <ConfirmActionModal
          title='Cancel Confirmation'
          message='Are you sure you want to cancel this order?'
          successMessage={`${
            selectedCancelOrder.action.isBuy ? 'Security buy' : 'Security sell'
          } order was successfully cancelled.`}
          errorMessage={cancelOrderError?.message}
          isOpen={isCancelOrderModalOpen}
          isError={cancelOrderFailed}
          isSuccess={cancelOrderSuccess}
          isLoading={isCancelOrderLoading}
          onCancel={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
          }}
          onOk={() => {
            onConfirmCancelOrder(selectedCancelOrder);
          }}
          onClose={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
            dispatch(doClearCancelOrder());
          }}
        />
      )}
      {selectedChangeOrder && (
        <ChangeOrderModal open order={selectedChangeOrder} accountId={account.accountId} onClose={onCloseChangeOrder} />
      )}
    </Row>
  );
};
